import React, { useEffect } from 'react';
import MetaTags from '../components/SEO/MetaTags';
import {
  ShoppingCartIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  ComputerDesktopIcon,
  HeartIcon,
  BriefcaseIcon,
  HomeIcon,
  AcademicCapIcon,
  ScaleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Industries = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const industries = [
    {
      icon: ShoppingCartIcon,
      name: 'Retail & E-commerce',
      description: 'Complete financial management for retail businesses including GST compliance, inventory management, and tax optimization.',
      challenges: [
        'Complex GST calculations for multiple products',
        'Inventory valuation and management',
        'E-commerce platform integrations',
        'Multi-state sales tax compliance',
      ],
      solutions: [
        'Automated GST return filing',
        'Inventory management systems',
        'E-commerce accounting integration',
        'Multi-state compliance management',
      ],
      caseStudy: {
        client: "Kerala Mart Online",
        result: "40% tax savings through strategic planning",
        testimonial: "NAQD's expertise in e-commerce taxation saved us significant money and streamlined our operations."
      }
    },
    {
      icon: TruckIcon,
      name: 'Transportation & Logistics',
      description: 'Specialized services for transport companies including fuel tax credits, vehicle depreciation, and operational cost optimization.',
      challenges: [
        'Fuel tax credit calculations',
        'Vehicle depreciation schedules',
        'Inter-state transport permits',
        'Driver payroll compliance',
      ],
      solutions: [
        'Fuel GST credit optimization',
        'Strategic asset depreciation',
        'Permit and license management',
        'Comprehensive payroll solutions',
      ],
      caseStudy: {
        client: "Malabar Logistics",
        result: "₹25 lakhs annual tax savings",
        testimonial: "Their understanding of transport industry specifics helped us save significantly on taxes."
      }
    },
    {
      icon: BuildingStorefrontIcon,
      name: 'Real Estate & Construction',
      description: 'Expert handling of complex real estate transactions, project financing, and construction accounting.',
      challenges: [
        'Project cost allocation',
        'GST on under-construction properties',
        'Joint development agreements',
        'RERA compliance',
      ],
      solutions: [
        'Project-based accounting',
        'GST optimization for developers',
        'JDA structuring and taxation',
        'Complete RERA compliance',
      ],
      caseStudy: {
        client: "Green Valley Developers",
        result: "30% reduction in project costs",
        testimonial: "NAQD's construction expertise helped us optimize costs and ensure full compliance."
      }
    },
    {
      icon: ComputerDesktopIcon,
      name: 'IT & Technology',
      description: 'Specialized services for tech companies including R&D tax credits, software licensing, and international tax planning.',
      challenges: [
        'R&D expense recognition',
        'Software licensing revenue',
        'International tax implications',
        'ESOP taxation',
      ],
      solutions: [
        'R&D tax credit optimization',
        'Revenue recognition strategies',
        'Cross-border tax planning',
        'ESOP structuring',
      ],
      caseStudy: {
        client: "TechStart Kerala",
        result: "₹15 lakhs in R&D tax credits",
        testimonial: "Their tech industry knowledge helped us claim significant R&D benefits."
      }
    },
    {
      icon: HeartIcon,
      name: 'Healthcare & Pharmaceuticals',
      description: 'Comprehensive services for healthcare providers including medical equipment depreciation and pharmaceutical compliance.',
      challenges: [
        'Medical equipment depreciation',
        'Pharmaceutical licensing',
        'Healthcare service taxation',
        'Clinical trial accounting',
      ],
      solutions: [
        'Optimized depreciation schedules',
        'Regulatory compliance management',
        'Service tax optimization',
        'Clinical accounting systems',
      ],
      caseStudy: {
        client: "Kerala Medical Center",
        result: "25% improvement in financial efficiency",
        testimonial: "Specialized healthcare accounting made our operations much more efficient."
      }
    },
    {
      icon: BriefcaseIcon,
      name: 'Manufacturing',
      description: 'End-to-end financial management for manufacturing units including cost accounting, GST optimization, and regulatory compliance.',
      challenges: [
        'Complex cost accounting',
        'Multi-stage GST calculations',
        'Inventory management',
        'Factory compliance',
      ],
      solutions: [
        'Activity-based costing',
        'GST optimization strategies',
        'Integrated inventory systems',
        'Comprehensive compliance management',
      ],
      caseStudy: {
        client: "Malabar Industries",
        result: "35% reduction in compliance costs",
        testimonial: "Their manufacturing expertise transformed our financial operations."
      }
    },
    {
      icon: HomeIcon,
      name: 'Hospitality & Tourism',
      description: 'Specialized accounting for hotels, restaurants, and tourism businesses including seasonal cash flow management.',
      challenges: [
        'Seasonal revenue fluctuations',
        'Multiple tax rates for services',
        'Tour operator licensing',
        'Food safety compliance',
      ],
      solutions: [
        'Seasonal cash flow planning',
        'Service tax optimization',
        'License management',
        'Food safety compliance',
      ],
      caseStudy: {
        client: "Backwater Resorts",
        result: "50% improvement in cash flow management",
        testimonial: "Expert handling of seasonal business challenges."
      }
    },
    {
      icon: AcademicCapIcon,
      name: 'Education & Training',
      description: 'Financial services for educational institutions including trust management, fee structuring, and compliance.',
      challenges: [
        'Trust accounting',
        'Fee structure optimization',
        'Educational service taxation',
        'Grant compliance',
      ],
      solutions: [
        'Trust fund management',
        'Strategic fee structuring',
        'Educational tax benefits',
        'Grant compliance management',
      ],
      caseStudy: {
        client: "Kerala International School",
        result: "Streamlined financial operations",
        testimonial: "Comprehensive education sector expertise."
      }
    },
    {
      icon: ScaleIcon,
      name: 'Legal & Professional Services',
      description: 'Specialized services for law firms, consultants, and professional service providers.',
      challenges: [
        'Professional service taxation',
        'Retainer fee management',
        'Client trust accounting',
        'Partnership structures',
      ],
      solutions: [
        'Service tax optimization',
        'Retainer management systems',
        'Trust accounting solutions',
        'Partnership structuring',
      ],
      caseStudy: {
        client: "Malabar Legal Associates",
        result: "Optimized partnership taxation",
        testimonial: "Deep understanding of professional service needs."
      }
    },
  ];

  return (
    <div>
      <MetaTags
        title="Industries We Serve - Specialized Financial Solutions | NAQD Consulting"
        description="Expert financial services for retail, transportation, real estate, IT, healthcare, manufacturing, hospitality, education, and legal sectors in Kerala."
        keywords="industry specific accounting, retail tax services, healthcare finance, real estate accounting, manufacturing compliance, hospitality financial services"
      />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-naqd-dark to-naqd-dark-gray">
        <div className="container-custom text-center">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6">
              Industries We <span className="text-gradient">Serve</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Specialized financial solutions tailored for Kerala's key industries.
              Deep sector expertise ensures optimized outcomes for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="card animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-naqd-green/10 rounded-full mb-4">
                  <industry.icon className="w-8 h-8 text-naqd-green" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{industry.name}</h3>
                <p className="text-sm text-gray-300 mb-4">{industry.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-2">Key Challenges:</h4>
                  <ul className="space-y-1">
                    {industry.challenges.slice(0, 2).map((challenge, idx) => (
                      <li key={idx} className="text-xs text-gray-400 flex items-center">
                        <CheckCircleIcon className="w-3 h-3 text-red-400 mr-1" />
                        {challenge}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-2">Our Solutions:</h4>
                  <ul className="space-y-1">
                    {industry.solutions.slice(0, 2).map((solution, idx) => (
                      <li key={idx} className="text-xs text-gray-400 flex items-center">
                        <CheckCircleIcon className="w-3 h-3 text-green-400 mr-1" />
                        {solution}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <p className="text-xs text-gray-400 mb-1">
                    <span className="font-semibold">Case Study:</span> {industry.caseStudy.client}
                  </p>
                  <p className="text-xs text-naqd-green font-semibold">
                    {industry.caseStudy.result}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Solutions */}
      <section className="section-padding bg-naqd-dark-gray/50">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              Custom <span className="text-gradient">Industry Solutions</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Every industry has unique financial challenges. We develop custom solutions
              that address your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card animate-on-scroll">
              <h3 className="text-xl font-semibold mb-4">Sector-Specific Expertise</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Deep understanding of industry regulations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Tailored compliance solutions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Industry-specific tax optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Proactive regulatory updates</span>
                </li>
              </ul>
            </div>

            <div className="card animate-on-scroll">
              <h3 className="text-xl font-semibold mb-4">Benefits for Your Business</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Reduced compliance costs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Improved cash flow management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Strategic tax planning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-naqd-green mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">Risk mitigation strategies</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-naqd-green text-white">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Industry-Specific Financial Solutions
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss how our industry expertise can benefit your business.
              Get a customized solution for your sector.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="bg-white text-naqd-green font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors">
                Get Industry Quote
              </a>
              <a href="tel:+919074286763" className="border-2 border-white text-white font-semibold py-3 px-6 rounded-lg hover:bg-white hover:text-naqd-green transition-colors">
                Call: +91 90742 86763
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;